<template>
    <div class="auction-create-modal">
        <div class="auction-create-modal__title">Выберите автомобиль</div>

        <FilterPanel
            class="auction-create-modal__filter-panel"
            :columns="4"
            @search="onSearch"
            @reset="onReset"
        >
            <UIInput
                id="vin"
                v-model="carsFilter.vin"
                label="VIN"
            />

            <UISelect
                id="dealer_id"
                v-model="carsFilter.dealer_id"
                :options="dealerOptions"
                label="Дилер"
                multiple
                searchable
            />

            <UISelect
                id="brand_id"
                v-model="carsBrandIdFilter"
                :options="brandOptions"
                label="Марка"
            />

            <UISelect
                id="model_id"
                v-model="carsFilter.model_id"
                :options="modelOptions"
                label="Модель"
                multiple
                searchable
            />

            <UISelect
                id="admission_type"
                v-model="carsAdmissionTypeFilter"
                :options="admissionTypeOptions"
                label="Тип поступления"
            />

            <UISelect
                id="nds_type"
                v-model="carsFilter.nds_type"
                :options="ndsTypeOptions"
                label="Тип НДС"
                multiple
            />

            <UIRange
                v-model="carsProductionYearFilter"
                :min="CarMinProductionYear"
                :max="CarMaxProductionYear"
                :interval="1"
                label="Год выпуска"
                :format-numbers="false"
            />

            <UIRange
                v-model="carsStartPriceFilter"
                :min="0"
                :max="CarMaxPrice"
                :interval="CarRangePriceStep"
                unit="₽"
                :format-numbers="true"
                label="Цена"
            />
        </FilterPanel>

        <UISearchResults
            :count="carsPaginationMeta?.pagination?.total ?? 0"
            :initial-sort="selectedSort"
            :sort-options="sortOptions"
            @update:sort="onSortUpdate"
        />

        <div v-if="!isCarsLoading && arCars.length > 0" class="cars-list__content">
            <CarListItem
                v-for="car in arCars"
                :key="'car-' + car.id"
                :car="car"
                :is-active="selectedCars.find((selectedCar: Car) => selectedCar.id === car.id) !== undefined"
                @added="onAddedCar"
                @removed="onRemovedCar"
            />
        </div>

        <div v-if="!isCarsLoading && arCars.length === 0" class="cars-list__content">
            <UINotFoundData />
        </div>

        <div v-if="isCarsLoading" class="cars-list__content">
            <UILoader class="listable" />
        </div>

        <TablePagination
            class="auction-create-modal__pagination"
            :total-pages="carsPaginationMeta?.pagination?.last_page ?? 1"
            :current-page="carsPaginationMeta?.pagination?.current_page ?? 1"
            :items-per-page="carsPaginationMeta?.pagination?.per_page ?? 10"
            @page-change="changePage"
        />
    </div>
</template>

<script lang="ts" setup>
    import { computed, onMounted, PropType, ref } from 'vue';

    import { SelectOption, SortOption } from '@/types/form.type.ts';
    import {
        CarMinProductionYear,
        CarMaxProductionYear,
        CarMaxPrice,
        CarsRequestFilter,
        getCarAdmissionTypeSelectOptions,
        getCarNdsTypeSelectOptions,
        CarStatus,
        Car,
        CarAdmissionType,
        CarRangePriceStep,
        CarMaxMileage,
    } from '@/types/car.type.ts';
    import { DealerListItem } from '@/types/dealer.type.ts';
    import { Model } from '@/types/model.type.ts';

    import useCarRepository from '@/repositories/car/useCarRepository.ts';
    import useDealerRepository from '@/repositories/dealer/useDealerRepository.ts';
    import useModelRepository from '@/repositories/model/useModelRepository.ts';

    import { getBrandSelectOptions } from '@/utils/SelectOptionFillers.ts';

    import FilterPanel from '@/components/shared/FilterPanel/FilterPanel.vue';
    import TablePagination from '@/components/shared/TablePagination/TablePagination.vue';

    import UIInput from '@/components/ui/UIInput/UIInput.vue';
    import UILoader from '@/components/ui/UILoader/UILoader.vue';
    import UINotFoundData from '@/components/ui/UINotFoundData/UINotFoundData.vue';
    import UISearchResults from '@/components/ui/UISearchResults/UISearchResults.vue';
    import UISelect from '@/components/ui/UISelect/UISelect.vue';
    import UIRange from '@/components/ui/UIRange/UIRange.vue';

    import CarListItem from '@/modals/AddCarToAuctionModal/components/CarListItem/CarListItem.vue';

    const { initialCars } = defineProps({
        initialCars: {
            type: Array as PropType<Car[]>,
            default: [],
        },
    });

    const { getAllActiveModels } = useModelRepository();
    const { getAllActiveDealers } = useDealerRepository();
    const { arCars, carsPaginationMeta, isCarsLoading, getCars } = useCarRepository();

    const selectedCars = ref<Car[]>(initialCars);
    const carsFilter = ref<CarsRequestFilter>({
        vin: '',
        dealer_id: [],
        brand_id: [],
        model_id: [],
        admission_type: [],
        nds_type: [],
        production_year: [CarMinProductionYear, CarMaxProductionYear],
        mileage: [0, CarMaxMileage],
        start_price: [0, CarMaxPrice],
        page: 1,
        sort: [{ created_at: 'asc' }],
    });
    const dealerOptions = ref<SelectOption[]>([]);
    const brandOptions = ref<SelectOption[]>(getBrandSelectOptions());
    const admissionTypeOptions = ref<SelectOption[]>(getCarAdmissionTypeSelectOptions());
    const ndsTypeOptions = ref<SelectOption[]>(getCarNdsTypeSelectOptions());
    const selectedSort = ref<string>('created_at_asc');
    const sortOptions = ref<SortOption[]>([
        { label: 'по дате добавления (1-9)', value: 'created_at_asc' },
        { label: 'по дате добавления (9-1)', value: 'created_at_desc' },
        { label: 'по пробегу (1-9)', value: 'mileage_asc' },
        { label: 'по пробегу (9-1)', value: 'mileage_desc' },
        { label: 'по году выпуска (1-9)', value: 'production_year_asc' },
        { label: 'по году выпуска (9-1)', value: 'production_year_desc' },
    ]);
    const arModels = ref<Model[]>([]);

    const carsBrandIdFilter = computed<number | undefined>({
        get() {
            return carsFilter.value.brand_id === undefined || carsFilter.value.brand_id.length === 0
                ? undefined
                : carsFilter.value.brand_id[0];
        },
        set(brandId?: number) {
            carsFilter.value.brand_id = brandId === undefined ? [] : [brandId];
        },
    });

    const carsAdmissionTypeFilter = computed({
        get() {
            return carsFilter.value.admission_type === undefined || carsFilter.value.admission_type.length === 0
                ? undefined
                : carsFilter.value.admission_type[0];
        },
        set(admissionType?: CarAdmissionType) {
            carsFilter.value.admission_type = admissionType === undefined ? [] : [admissionType];
        },
    });

    const carsProductionYearFilter = computed<number[]>({
        get: () => ([
            carsFilter.value.production_year
                ? carsFilter.value.production_year[0]
                : CarMinProductionYear,
            carsFilter.value.production_year
                ? carsFilter.value.production_year[1]
                : CarMaxProductionYear,
        ]),
        set: (range: number[]) => {
            carsFilter.value.production_year = [range[0], range[1]];
        },
    });

    const carsStartPriceFilter = computed<number[]>({
        get: () => ([
            carsFilter.value.start_price
                ? carsFilter.value.start_price[0]
                : 0,
            carsFilter.value.start_price
                ? carsFilter.value.start_price[1]
                : CarMaxPrice,
        ]),
        set: (range: number[]) => {
            carsFilter.value.start_price = [range[0], range[1]];
        },
    });

    const modelOptions = computed<SelectOption[]>(() => {
        return arModels.value
            .filter((model: Model) => {
                if (carsFilter.value.brand_id === undefined || carsFilter.value.brand_id.length === 0) {
                    return true;
                }

                return carsFilter.value.brand_id.indexOf(model.brand_id) >= 0;
            })
            .map((model: Model) => {
                return {
                    label: model.name,
                    value: model.id,
                };
            });
    });

    function onSearch(): void {
        fetchCars();
    }

    function onReset(): void {
        carsFilter.value = {
            vin: '',
            dealer_id: [],
            brand_id: [],
            model_id: [],
            admission_type: [],
            nds_type: [],
            production_year: [CarMinProductionYear, CarMaxProductionYear],
            mileage: [0, CarMaxMileage],
            start_price: [0, CarMaxPrice],
            page: 1,
            sort: carsFilter.value.sort,
        };

        fetchCars();
    }

    function changePage(page: number): void {
        carsFilter.value.page = page;
        fetchCars();
    }

    function onSortUpdate(sortValue: string | number): void {
        selectedSort.value = sortValue.toString();

        switch (sortValue) {
            case 'created_at_asc':
                carsFilter.value.sort = [{ created_at: 'asc' }];
                break;
            case 'created_at_desc':
                carsFilter.value.sort = [{ created_at: 'desc' }];
                break;
            case 'production_year_asc':
                carsFilter.value.sort = [{ production_year: 'asc' }];
                break;
            case 'production_year_desc':
                carsFilter.value.sort = [{ production_year: 'desc' }];
                break;
            case 'mileage_asc':
                carsFilter.value.sort = [{ mileage: 'asc' }];
                break;
            case 'mileage_desc':
                carsFilter.value.sort = [{ mileage: 'desc' }];
                break;
        }

        carsFilter.value.page = 1;
        fetchCars();
    }

    function onAddedCar(car: Car): void {
        const carIndex = selectedCars.value.findIndex((foundCar: Car) => foundCar.id === car.id);

        if (carIndex < 0) {
            selectedCars.value.push({ ...car });
        }
    }

    function onRemovedCar(car: Car): void {
        const carIndex = selectedCars.value.findIndex((foundCar: Car) => foundCar.id === car.id);

        if (carIndex >= 0) {
            selectedCars.value.splice(carIndex, 1);
        }
    }

    function mapCarsFilterToRequestFilter(): CarsRequestFilter {
        const requestFilter: CarsRequestFilter = {
            production_year: [CarMinProductionYear, CarMaxProductionYear],
            mileage: [0, CarMaxMileage],
            start_price: [0, CarMaxPrice],
            status: [CarStatus.Approved],
            page: carsFilter.value.page,
            sort: carsFilter.value.sort,
        };

        if (carsFilter.value.vin) {
            requestFilter.vin = carsFilter.value.vin;
        }

        if (carsFilter.value.dealer_id) {
            requestFilter.dealer_id = carsFilter.value.dealer_id;
        }

        if (carsFilter.value.brand_id) {
            requestFilter.brand_id = carsFilter.value.brand_id;
        }

        if (carsFilter.value.model_id) {
            requestFilter.model_id = carsFilter.value.model_id;
        }

        if (carsFilter.value.admission_type) {
            requestFilter.admission_type = carsFilter.value.admission_type;
        }

        if (carsFilter.value.nds_type) {
            requestFilter.nds_type = carsFilter.value.nds_type;
        }

        if (carsFilter.value.production_year !== undefined) {
            requestFilter.production_year = carsFilter.value.production_year;
        }

        if (carsFilter.value.start_price !== undefined) {
            requestFilter.start_price = carsFilter.value.start_price;
        }

        return requestFilter;
    }

    function fetchFilterOptions(): void {
        getAllActiveModels().then((models: Model[]) => {
            arModels.value = models;
        });

        getAllActiveDealers().then((dealers: DealerListItem[]) => {
            dealerOptions.value = dealers.map((dealer: DealerListItem) => ({
                label: dealer.name + ' (' + dealer.sap_code + ')',
                value: dealer.id,
            }));
        });
    }

    function fetchCars(): void {
        getCars(mapCarsFilterToRequestFilter());
    }

    onMounted(() => {
        fetchFilterOptions();
        fetchCars();
    });
</script>

<style scoped lang="scss">
    @use './AddCarToAuctionModal.scss' as *;
</style>
